"use client";

import type { ReactElement } from "react";
import { useMemo } from "react";

import { InfoTooltip } from "@/components/shared/info-tooltip";
import { DEFAULT_DATE_FORMAT, FocusDateParser } from "@/utils/date";
import { cn } from "@/utils/lib";

type TimestampTooltipProps = {
  timestamp?: string;
  className?: string;
  children?: ReactElement;
  leadingText?: string;
};

export const TimestampTooltip = ({
  timestamp,
  className = "",
  children,
  leadingText = "",
}: TimestampTooltipProps) => {
  const [relativeDate, absoluteDate] = useMemo(() => {
    if (!timestamp) return ["", ""];

    const relativeDate = FocusDateParser.formatUTCDateFromNowShort(timestamp, {
      forcePast: true,
    });
    const absoluteDate = FocusDateParser.formatUTCDate(
      timestamp,
      DEFAULT_DATE_FORMAT,
    );
    return [relativeDate, absoluteDate];
  }, [timestamp]);

  if (!timestamp) return null;

  return (
    <InfoTooltip
      text={
        <span className={cn("text-xs text-muted", className)}>
          {absoluteDate}
        </span>
      }
    >
      {children || (
        <span
          className={cn(
            "inline-block text-xs leading-[12px] text-muted",
            className,
          )}
        >
          {leadingText ? `${leadingText} ` : ""}
          {relativeDate}
        </span>
      )}
    </InfoTooltip>
  );
};
